import type { Writable } from "svelte/store";
import type { AzureBlobConfig } from "./fs";
import { FS } from "./fs";
import { s3SanitizeDir, s3SanitizePath } from "./fs-s3";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Azure Blob Storage sends no CORS headers by default, so calls go through
// /api/azureblob. The account key is a request header.

class FileSystemAzureBlob extends FS {
  constructor(config: AzureBlobConfig) {
    super(config);
  }

  get azureConfig(): AzureBlobConfig {
    return this.config as AzureBlobConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await azureList(this.azureConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let url = azureURL(this.azureConfig, "/api/azureblob/getpublicurl", "path", path);
    let js = await azureCall(this.azureConfig, url);
    if (!js.url) {
      throw new Error("azure did not return a url");
    }
    return mkURL("https://workers.filerion.com/api/fetch", "url", js.url);
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    path = s3SanitizePath(path);
    let url = azureURL(this.azureConfig, "/api/azureblob/upload", "path", path);
    await azureSend("PUT", url, this.azureConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = azureURL(this.azureConfig, "/api/azureblob/rename", "path", path, "newPath", newPath);
    await azureCall(this.azureConfig, url);
  }

  async createDirectory(path: string): Promise<void> {
    let url = azureURL(this.azureConfig, "/api/azureblob/createdirectory", "path", path);
    await azureCall(this.azureConfig, url);
  }

  async deleteFile(path: string): Promise<void> {
    path = s3SanitizePath(path);
    let url = azureURL(this.azureConfig, "/api/azureblob/deletefile", "path", path);
    await azureCall(this.azureConfig, url);
  }
}

export async function testAzureBlobConnection(config: AzureBlobConfig): Promise<void> {
  await azureCall(config, azureURL(config, "/api/azureblob/test"));
}

async function azureList(config: AzureBlobConfig, dir: string): Promise<any[][]> {
  dir = s3SanitizeDir(dir);
  let js = await azureCall(config, azureURL(config, "/api/azureblob/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("azure list failed");
  }
  return js;
}

function azureURL(config: AzureBlobConfig, path: string, ...args: string[]): string {
  return mkURL(path, "account", config.account, "container", config.container, ...args);
}

function azureHeaders(config: AzureBlobConfig): Record<string, string> {
  return {
    "X-Azure-Key": config.key,
  };
}

async function azureCall(config: AzureBlobConfig, url: string): Promise<any> {
  let rsp = await fetch(url, { headers: azureHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `azure ${rsp.status}`);
  }
  return js;
}

function azureSend(
  method: string,
  url: string,
  config: AzureBlobConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
): Promise<void> {
  let headers = azureHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(azureErrText(xhr)));
    };
    xhr.onerror = () => {
      reject(new Error("azure request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      xhr.upload.onprogress = (ev) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

function azureErrText(xhr: XMLHttpRequest): string {
  let text = xhr.responseText || "";
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `azure ${xhr.status}`;
}

export function makeFileSystemAzureBlob(config: AzureBlobConfig): FileSystemAzureBlob {
  return new FileSystemAzureBlob(config);
}
